import { ChatMessage, WidgetTheme } from '../types'

interface ChatWindowProps {
  theme: WidgetTheme
  messages: ChatMessage[]
  isLoading: boolean
  welcomeMessage: string
  placeholder: string
  onSendMessage: (message: string) => void
  onClose: () => void
  onNewChat?: () => void
}

// Helper function to adjust color brightness
function adjustBrightness(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
}

export function createChatWindow({
  theme,
  messages,
  isLoading,
  welcomeMessage,
  placeholder,
  onSendMessage,
  onClose,
  onNewChat,
}: ChatWindowProps): HTMLElement {
  const container = document.createElement('div')
  container.className = 'chat-window'
  
  // Apply container styles with improved design
  Object.assign(container.style, {
    position: 'fixed',
    bottom: '90px',
    right: '20px',
    width: '380px',
    height: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.fontFamily,
    zIndex: '10000',
    overflow: 'hidden',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    backdropFilter: 'blur(20px)',
    animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  })

  // Header with improved design
  const header = document.createElement('div')
  Object.assign(header.style, {
    background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${adjustBrightness(theme.primaryColor, -10)} 100%)`,
    color: '#ffffff',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    position: 'relative',
  })

  // Add subtle pattern overlay
  const headerOverlay = document.createElement('div')
  Object.assign(headerOverlay.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  })
  header.appendChild(headerOverlay)

  const headerTitle = document.createElement('div')
  headerTitle.style.position = 'relative'
  headerTitle.innerHTML = `
    <div style="display: flex; align-items: center; gap: 12px;">
      <div style="width: 40px; height: 40px; background: rgba(255, 255, 255, 0.2); border-radius: 50%; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px);">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div>
        <h3 style="margin: 0; font-size: 18px; font-weight: 700; letter-spacing: -0.025em;">AI Assistant</h3>
        <p style="margin: 0; font-size: 13px; opacity: 0.9; font-weight: 500;">
          <span style="display: inline-block; width: 8px; height: 8px; background: #10b981; border-radius: 50%; margin-right: 6px; animation: pulse 2s infinite;"></span>
          Online
        </p>
      </div>
    </div>
  `

  // Header buttons container
  const headerButtons = document.createElement('div')
  Object.assign(headerButtons.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    position: 'relative',
    zIndex: '1',
  })

  // New Chat button (only show if there are messages and callback is provided)
  if (messages.length > 0 && onNewChat) {
    const newChatButton = document.createElement('button')
    Object.assign(newChatButton.style, {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      color: '#ffffff',
      cursor: 'pointer',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s ease',
      backdropFilter: 'blur(10px)',
    })
    newChatButton.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      New
    `
    newChatButton.addEventListener('click', () => {
      if (confirm('Start a new conversation? This will clear your current chat history.')) {
        onNewChat()
      }
    })
    newChatButton.addEventListener('mouseenter', () => {
      newChatButton.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
      newChatButton.style.transform = 'scale(1.05)'
    })
    newChatButton.addEventListener('mouseleave', () => {
      newChatButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
      newChatButton.style.transform = 'scale(1)'
    })
    headerButtons.appendChild(newChatButton)
  }

  const closeButton = document.createElement('button')
  Object.assign(closeButton.style, {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    color: '#ffffff',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(10px)',
  })
  closeButton.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `
  closeButton.addEventListener('click', onClose)
  closeButton.addEventListener('mouseenter', () => {
    closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
    closeButton.style.transform = 'scale(1.1)'
  })
  closeButton.addEventListener('mouseleave', () => {
    closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
    closeButton.style.transform = 'scale(1)'
  })

  headerButtons.appendChild(closeButton)

  header.appendChild(headerTitle)
  header.appendChild(headerButtons)

  // Messages area with improved scrolling
  const messagesArea = document.createElement('div')
  messagesArea.className = 'messages-area'
  Object.assign(messagesArea.style, {
    flex: '1',
    padding: '20px',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: '#f8fafc',
    background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)',
    scrollBehavior: 'smooth',
    // Custom scrollbar
    scrollbarWidth: 'thin',
    scrollbarColor: '#cbd5e1 transparent',
  })

  // Add custom scrollbar styles
  const scrollbarStyle = document.createElement('style')
  scrollbarStyle.textContent = `
    .messages-area::-webkit-scrollbar {
      width: 6px;
    }
    .messages-area::-webkit-scrollbar-track {
      background: transparent;
    }
    .messages-area::-webkit-scrollbar-thumb {
      background-color: #cbd5e1;
      border-radius: 3px;
    }
    .messages-area::-webkit-scrollbar-thumb:hover {
      background-color: #94a3b8;
    }
  `
  document.head.appendChild(scrollbarStyle)

  // Welcome message with better design
  if (messages.length === 0) {
    const welcomeDiv = document.createElement('div')
    Object.assign(welcomeDiv.style, {
      backgroundColor: '#ffffff',
      padding: '16px 20px',
      borderRadius: '16px 16px 16px 4px',
      marginBottom: '16px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)',
      maxWidth: '85%',
      fontSize: '15px',
      lineHeight: '1.5',
      color: '#374151',
      border: '1px solid #f3f4f6',
      position: 'relative',
    })
    
    // Add subtle gradient border
    const borderDiv = document.createElement('div')
    Object.assign(borderDiv.style, {
      position: 'absolute',
      top: '-1px',
      left: '-1px',
      right: '-1px',
      bottom: '-1px',
      background: `linear-gradient(135deg, ${theme.primaryColor}20, transparent)`,
      borderRadius: '16px 16px 16px 4px',
      zIndex: '-1',
    })
    welcomeDiv.appendChild(borderDiv)
    
    welcomeDiv.textContent = welcomeMessage
    messagesArea.appendChild(welcomeDiv)
  }

  // Render messages with improved design
  messages.forEach((message, index) => {
    const messageDiv = document.createElement('div')
    Object.assign(messageDiv.style, {
      marginBottom: '16px',
      display: 'flex',
      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
      alignItems: 'flex-end',
      gap: '8px',
    })

    // Add avatar for bot messages
    if (message.role === 'assistant') {
      const avatar = document.createElement('div')
      Object.assign(avatar.style, {
        width: '32px',
        height: '32px',
        backgroundColor: theme.primaryColor,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: '0',
        marginBottom: '2px',
      })
      avatar.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
      messageDiv.appendChild(avatar)
    }

    const messageBubble = document.createElement('div')
    const isUser = message.role === 'user'
    Object.assign(messageBubble.style, {
      backgroundColor: isUser ? theme.primaryColor : '#ffffff',
      color: isUser ? '#ffffff' : '#374151',
      padding: '12px 16px',
      borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
      maxWidth: '75%',
      boxShadow: isUser 
        ? `0 4px 12px ${theme.primaryColor}25, 0 2px 4px rgba(0, 0, 0, 0.1)` 
        : '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)',
      fontSize: '15px',
      lineHeight: '1.5',
      border: isUser ? 'none' : '1px solid #f3f4f6',
      wordWrap: 'break-word',
      position: 'relative',
      animation: `messageSlide 0.3s ease-out ${index * 0.1}s both`,
    })

    // Add subtle glow for user messages
    if (isUser) {
      messageBubble.style.background = `linear-gradient(135deg, ${theme.primaryColor} 0%, ${adjustBrightness(theme.primaryColor, -5)} 100%)`
    }

    messageBubble.textContent = message.content
    messageDiv.appendChild(messageBubble)
    messagesArea.appendChild(messageDiv)
  })

  // Loading indicator with better animation
  if (isLoading) {
    const loadingDiv = document.createElement('div')
    Object.assign(loadingDiv.style, {
      display: 'flex',
      justifyContent: 'flex-start',
      marginBottom: '16px',
      alignItems: 'flex-end',
      gap: '8px',
    })

    // Bot avatar for loading
    const avatar = document.createElement('div')
    Object.assign(avatar.style, {
      width: '32px',
      height: '32px',
      backgroundColor: theme.primaryColor,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: '0',
      marginBottom: '2px',
    })
    avatar.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `

    const loadingBubble = document.createElement('div')
    Object.assign(loadingBubble.style, {
      backgroundColor: '#ffffff',
      padding: '16px 20px',
      borderRadius: '16px 16px 16px 4px',
      maxWidth: '75%',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #f3f4f6',
    })
    loadingBubble.innerHTML = `
      <div style="display: flex; gap: 6px; align-items: center;">
        <div style="width: 8px; height: 8px; background: linear-gradient(45deg, ${theme.primaryColor}, ${adjustBrightness(theme.primaryColor, 20)}); border-radius: 50%; animation: typingPulse 1.4s ease-in-out infinite;"></div>
        <div style="width: 8px; height: 8px; background: linear-gradient(45deg, ${theme.primaryColor}, ${adjustBrightness(theme.primaryColor, 20)}); border-radius: 50%; animation: typingPulse 1.4s ease-in-out 0.2s infinite;"></div>
        <div style="width: 8px; height: 8px; background: linear-gradient(45deg, ${theme.primaryColor}, ${adjustBrightness(theme.primaryColor, 20)}); border-radius: 50%; animation: typingPulse 1.4s ease-in-out 0.4s infinite;"></div>
        <span style="margin-left: 8px; font-size: 13px; color: #6b7280; font-style: italic;">AI is typing...</span>
      </div>
    `
    
    loadingDiv.appendChild(avatar)
    loadingDiv.appendChild(loadingBubble)
    messagesArea.appendChild(loadingDiv)
  }

  // Input area with modern design
  const inputArea = document.createElement('div')
  Object.assign(inputArea.style, {
    padding: '20px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    background: 'linear-gradient(to top, #ffffff 0%, #fafbfc 100%)',
  })

  const inputContainer = document.createElement('div')
  Object.assign(inputContainer.style, {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    backgroundColor: '#f8fafc',
    borderRadius: '24px',
    padding: '8px 8px 8px 20px',
    border: '1px solid #e2e8f0',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
  })

  const input = document.createElement('input')
  Object.assign(input.style, {
    flex: '1',
    padding: '12px 0',
    border: 'none',
    borderRadius: '0',
    outline: 'none',
    fontSize: '15px',
    fontFamily: theme.fontFamily,
    backgroundColor: 'transparent',
    color: '#374151',
    lineHeight: '1.5',
  })
  input.type = 'text'
  input.placeholder = placeholder

  // Input focus effects
  input.addEventListener('focus', () => {
    inputContainer.style.borderColor = theme.primaryColor
    inputContainer.style.boxShadow = `0 0 0 3px ${theme.primaryColor}15, 0 4px 8px rgba(0, 0, 0, 0.05)`
  })
  input.addEventListener('blur', () => {
    inputContainer.style.borderColor = '#e2e8f0'
    inputContainer.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)'
  })

  const sendButton = document.createElement('button')
  Object.assign(sendButton.style, {
    backgroundColor: theme.primaryColor,
    color: '#ffffff',
    border: 'none',
    borderRadius: '50%',
    width: '44px',
    height: '44px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    flexShrink: '0',
    background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${adjustBrightness(theme.primaryColor, -10)} 100%)`,
    boxShadow: `0 4px 12px ${theme.primaryColor}25, 0 2px 4px rgba(0, 0, 0, 0.1)`,
  })
  sendButton.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M22 2L11 13" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `

  // Button hover effects
  sendButton.addEventListener('mouseenter', () => {
    sendButton.style.transform = 'scale(1.05)'
    sendButton.style.boxShadow = `0 6px 16px ${theme.primaryColor}35, 0 4px 8px rgba(0, 0, 0, 0.15)`
  })
  sendButton.addEventListener('mouseleave', () => {
    sendButton.style.transform = 'scale(1)'
    sendButton.style.boxShadow = `0 4px 12px ${theme.primaryColor}25, 0 2px 4px rgba(0, 0, 0, 0.1)`
  })

  // Handle message sending with auto-scroll
  const handleSend = () => {
    const message = input.value.trim()
    if (message) {
      // Check if user is near bottom before sending
      const isNearBottom = messagesArea.scrollHeight - messagesArea.scrollTop - messagesArea.clientHeight < 100
      
      onSendMessage(message)
      input.value = ''
      
      // Keep input focused for better UX
      input.focus()
      
      // Only scroll if user was already at the bottom (natural conversation flow)
      if (isNearBottom) {
        setTimeout(() => {
          messagesArea.scrollTo({
            top: messagesArea.scrollHeight,
            behavior: 'smooth'
          })
        }, 100)
      }
      // If user was scrolled up reading history, don't interrupt them
    }
  }

  sendButton.addEventListener('click', handleSend)
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      handleSend()
    }
  })

  inputContainer.appendChild(input)
  inputContainer.appendChild(sendButton)
  inputArea.appendChild(inputContainer)

  // Assemble the window
  container.appendChild(header)
  container.appendChild(messagesArea)
  container.appendChild(inputArea)

  // Add CSS animations
  const animationStyle = document.createElement('style')
  animationStyle.textContent = `
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    
    @keyframes messageSlide {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes typingPulse {
      0%, 60%, 100% {
        transform: scale(1);
        opacity: 0.5;
      }
      30% {
        transform: scale(1.2);
        opacity: 1;
      }
    }
    
    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `
  document.head.appendChild(animationStyle)

  // Auto-focus input when chat opens and preserve scroll position
  setTimeout(() => {
    input.focus()
    
    // Only scroll to bottom if it's a completely fresh chat (no messages at all)
    if (messages.length === 0 && !isLoading) {
      messagesArea.scrollTo({
        top: messagesArea.scrollHeight,
        behavior: 'auto' // Instant for initial load
      })
    }
  }, 100) // Reduced delay for better responsiveness

  return container
}
